import {
  ref,
  get,
  set,
  update,
  remove,
  onValue,
  query,
  orderByChild,
  equalTo,
  off,
} from 'firebase/database';
import { rtdb } from '../../config/firebase';
import type { ContentPage, ContentPageType } from '../../types';
import { dateToMs, msToDate, snapshotToArray, stripUndefined } from './helpers';

const PATH = 'contentPages';

type Unsubscribe = () => void;

const fromRtdb = (id: string, data: any): ContentPage => ({
  ...data,
  id,
  lastUpdatedAt: msToDate(data.lastUpdatedAt) as Date,
});

const toRtdb = (page: ContentPage): any => {
  const { id: _id, ...rest } = page;
  return {
    ...rest,
    lastUpdatedAt: dateToMs(page.lastUpdatedAt),
  };
};

export const getAllContentPages = async (): Promise<ContentPage[]> => {
  const snapshot = await get(ref(rtdb, PATH));
  return snapshotToArray<ContentPage>(snapshot.val(), fromRtdb);
};

export const getContentPageById = async (
  pageId: string
): Promise<ContentPage | null> => {
  const snapshot = await get(ref(rtdb, `${PATH}/${pageId}`));
  return snapshot.exists() ? fromRtdb(pageId, snapshot.val()) : null;
};

export const getContentPageByType = async (
  type: ContentPageType
): Promise<ContentPage | null> => {
  const q = query(ref(rtdb, PATH), orderByChild('type'), equalTo(type));
  const snapshot = await get(q);
  const pages = snapshotToArray<ContentPage>(snapshot.val(), fromRtdb);
  return pages[0] ?? null;
};

export const getPublishedContentPages = async (): Promise<ContentPage[]> => {
  const snapshot = await get(ref(rtdb, PATH));
  return snapshotToArray<ContentPage>(snapshot.val(), fromRtdb).filter(
    (p) => p.isPublished
  );
};

export const saveContentPage = async (page: ContentPage): Promise<void> => {
  await set(ref(rtdb, `${PATH}/${page.id}`), stripUndefined(toRtdb(page)));
};

export const updateContentPage = async (
  pageId: string,
  updates: Partial<ContentPage>
): Promise<void> => {
  const data: any = { ...updates };
  if (updates.lastUpdatedAt !== undefined) {
    data.lastUpdatedAt = dateToMs(updates.lastUpdatedAt);
  }
  await update(ref(rtdb, `${PATH}/${pageId}`), stripUndefined(data));
};

export const deleteContentPage = async (pageId: string): Promise<void> => {
  await remove(ref(rtdb, `${PATH}/${pageId}`));
};

export const subscribeToContentPages = (
  callback: (pages: ContentPage[]) => void,
  onError?: (error: Error) => void
): Unsubscribe => {
  const r = ref(rtdb, PATH);
  const handler = onValue(
    r,
    (snapshot) => {
      callback(snapshotToArray<ContentPage>(snapshot.val(), fromRtdb));
    },
    onError
  );
  return () => off(r, 'value', handler);
};

export const subscribeToContentPage = (
  pageId: string,
  callback: (page: ContentPage | null) => void
): Unsubscribe => {
  const r = ref(rtdb, `${PATH}/${pageId}`);
  const handler = onValue(r, (snapshot) => {
    callback(snapshot.exists() ? fromRtdb(pageId, snapshot.val()) : null);
  });
  return () => off(r, 'value', handler);
};

/**
 * Initialize default content pages if they don't exist
 */
export const initializeDefaultContentPages = async (
  adminUserId: string
): Promise<void> => {
  const defaultPages: ContentPage[] = [
    {
      id: 'arrival-departure',
      type: 'arrival-departure',
      title: { de: 'Anreise & Abreise', en: 'Arrival & Departure' },
      content: { de: '', en: '' },
      lastUpdatedAt: new Date(),
      lastUpdatedBy: adminUserId,
      isPublished: false,
    },
    {
      id: 'house-rules',
      type: 'house-rules',
      title: { de: 'Hausregeln', en: 'House Rules' },
      content: { de: '', en: '' },
      lastUpdatedAt: new Date(),
      lastUpdatedBy: adminUserId,
      isPublished: false,
    },
    {
      id: 'payment-terms',
      type: 'payment-terms',
      title: { de: 'Zahlungsmodalitäten', en: 'Payment Terms' },
      content: { de: '', en: '' },
      lastUpdatedAt: new Date(),
      lastUpdatedBy: adminUserId,
      isPublished: false,
    },
    {
      id: 'pricing-info',
      type: 'pricing-info',
      title: { de: 'Preisübersicht', en: 'Pricing Information' },
      content: { de: '', en: '' },
      lastUpdatedAt: new Date(),
      lastUpdatedBy: adminUserId,
      isPublished: false,
    },
  ];

  for (const page of defaultPages) {
    const existing = await getContentPageById(page.id);
    if (!existing) {
      await saveContentPage(page);
    }
  }
};
